import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { Typography } from "@mui/material";

import BoostSettingTableBase from "./BoostSettingTable";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { logger } from "@/helpers/loggerHelper";
import ConfirmBoostCampaignBase from "./confirm-boost/ConfirmBoostCampaign";
import BackLink from "@/components/BackLink";
import RpAlert from "@/components/alerts/RpAlert";
import { HTTP_STATUS } from "@/config/constants";
import { makeFirstCharCap } from "@/helpers/utils";
import type { BoostSelection } from "./confirm-boost/BoostConfirmLocation";

type BoostCampaignProps = {
  campaignId?: string | number;
};

type BoostCampaignLocation = {
  fk_location_id?: string | number;
  location_name?: string;
};

type BoostCampaignChannel = {
  id?: string | number;
  label?: string;
};

type BoostCampaignData = {
  id?: string | number;
  campaign_name?: string;
  campaign_objective?: string;
  campaignGoal?: string;
  objective?: string;
  is_campaign_boost?: boolean | number;
  is_selected_boost_type?: boolean | number;
  boost_payment_method?: string;
  boosted_duration?: number;
  channelNames?: BoostCampaignChannel[];
  locationData?: BoostCampaignLocation[];
  system_generated_campaign_id?: string;
};

type BoostLoggedInUser = {
  participantData?: {
    id?: string | number;
  };
};

type BoostGlobalContext = {
  loggedInUser?: BoostLoggedInUser | null;
};

const BoostSettingTable = BoostSettingTableBase as React.ComponentType<
  Record<string, any>
>;
const ConfirmBoostCampaign = ConfirmBoostCampaignBase as React.ComponentType<
  Record<string, any>
>;

function BoostCampaign({ campaignId }: BoostCampaignProps) {
  const { loggedInUser } = useContext(GlobalContext) as BoostGlobalContext;
  const [campaignData, setCampaignData] = useState<BoostCampaignData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [selections, setSelections] = useState<BoostSelection[] | null>(null);
  const [step, setStep] = useState(1);

  const fetchCampaignDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const reqData = {
        fk_campaign_id: campaignId,
        fk_participant_id: loggedInUser?.participantData?.id || "",
      };
      const res = await campaignService.getAllCampaignBoostLocations(reqData);
      if (res.status === HTTP_STATUS.OK) {
        const data = res.data;
        setCampaignData(data);
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId, loggedInUser]);

  useEffect(() => {
    fetchCampaignDetails();
  }, [fetchCampaignDetails]);

  const campaignHasOnlyPMaxChannel = useMemo(() => {
    if (!campaignData?.channelNames || campaignData?.channelNames?.length > 1)
      return false;
    return campaignData?.channelNames?.some(
      (c) => c?.label?.toLowerCase() === "pmax"
    );
  }, [campaignData?.channelNames]);

  const handleOnContinue = useCallback((data?: BoostSelection[]) => {
    setSelections(data?.reverse());
    setStep(2);
  }, []);

  if (isLoading) {
    return (
      <div className="d-flex-center">
        <Typography>Loading...</Typography>
      </div>
    );
  }

  if (campaignData && !campaignData?.is_campaign_boost) {
    return <Navigate to={`/dashboard`} replace />;
  }

  return (
    <>
      <div className="boost-campaign">
        <div className={step !== 1 ? "d-none" : ""}>
          <BackLink />
          <Typography
            component="h1"
            variant="h5"
            gutterBottom
            className="mt-2 boost-page-title"
          >
            {`Adjust Budget for: ${makeFirstCharCap(campaignData?.campaign_name)} Campaign`}
          </Typography>
          <Typography gutterBottom>
            You can adjust the budget of an active ad campaign to help increase
            its impact. You have the option to adjust all channels at once for
            broader reach, or focus your adjustments on selected channels using
            the dropdown selector.
          </Typography>
        </div>

        <div className={step !== 2 ? "d-none" : "mb-5"}>
          <Typography
            component="h1"
            variant="h5"
            color="text.primary"
            gutterBottom
          >
            Confirmation
          </Typography>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Review your selections and press confirm if it looks good
          </Typography>
        </div>

        {campaignData?.locationData?.length ? (
          <BoostSettingTable
            locationData={campaignData?.locationData}
            is_selected_boost_type={campaignData?.is_selected_boost_type}
            boost_payment_method={campaignData?.boost_payment_method}
            boosted_duration={campaignData?.boosted_duration}
            channelsList={campaignData?.channelNames}
            campaignId={campaignData?.id}
            campaignData={campaignData}
            onContinue={handleOnContinue}
            selections={selections}
            hide={step !== 1}
          />
        ) : (
          <div className="d-flex-center w-100 py-5">
            <RpAlert severity="info">
              <Typography variant="caption">
                No locations eligible for adjustment found for this campaign
              </Typography>
            </RpAlert>
          </div>
        )}

        {step === 2 && (
          <ConfirmBoostCampaign
            selections={selections}
            campaign={campaignData}
            onMakeChange={() => setStep(1)}
            campaignHasOnlyPMaxChannel={campaignHasOnlyPMaxChannel}
          />
        )}
      </div>
    </>
  );
}

export default BoostCampaign;
